import { Header } from "@/components/Header";
import { PremiumFooter } from "@/components/landing/PremiumFooter";
import { ShieldCheck, Download, Trash2, Edit, Eye, Ban } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";

const rights = [
  {
    icon: Eye,
    title: "Droit d'accès",
    description: "Vous pouvez demander une copie de toutes les données personnelles que nous détenons sur vous.",
  },
  {
    icon: Edit,
    title: "Droit de rectification",
    description: "Vous pouvez demander la correction de toute donnée inexacte ou incomplète.",
  },
  {
    icon: Trash2,
    title: "Droit à l'effacement",
    description: "Vous pouvez demander la suppression de vos données personnelles (droit à l'oubli).",
  },
  {
    icon: Download,
    title: "Droit à la portabilité",
    description: "Vous pouvez recevoir vos données dans un format structuré et lisible par machine.",
  },
  {
    icon: Ban,
    title: "Droit d'opposition",
    description: "Vous pouvez vous opposer au traitement de vos données pour certaines finalités.",
  },
  {
    icon: ShieldCheck,
    title: "Droit de limitation",
    description: "Vous pouvez demander la limitation du traitement dans certains cas.",
  },
];

const RGPD = () => {
  return (
    <div className="min-h-screen bg-background">
      <Header />

      <section className="pt-24 pb-16">
        <div className="container mx-auto px-6 max-w-4xl">
          <div className="text-center mb-12">
            <div className="w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-6">
              <ShieldCheck className="w-8 h-8 text-primary" />
            </div>
            <h1 className="text-4xl font-bold text-foreground mb-4 font-display">
              Conformité RGPD
            </h1>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Floop s'engage à respecter le Règlement Général sur la Protection des Données (RGPD). 
              Voici comment nous protégeons vos droits.
            </p>
          </div>

          {/* Your Rights */}
          <div className="mb-16">
            <h2 className="text-2xl font-bold text-foreground mb-8 text-center">Vos droits</h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
              {rights.map((right, index) => (
                <div
                  key={index}
                  className="bg-card border border-border rounded-2xl p-6"
                >
                  <div className="w-10 h-10 rounded-xl bg-primary/10 flex items-center justify-center mb-4">
                    <right.icon className="w-5 h-5 text-primary" />
                  </div>
                  <h3 className="font-semibold text-foreground mb-2">{right.title}</h3>
                  <p className="text-sm text-muted-foreground">{right.description}</p>
                </div>
              ))}
            </div>
          </div>

          {/* How to Exercise Rights */}
          <div className="bg-card border border-border rounded-2xl p-8 mb-12">
            <h2 className="text-2xl font-bold text-foreground mb-6">Comment exercer vos droits ?</h2>
            <div className="space-y-4 text-muted-foreground">
              <p>
                Pour exercer l'un de vos droits RGPD, vous pouvez :
              </p>
              <ul className="list-disc list-inside space-y-2">
                <li>Envoyer un email à notre DPO : <a href="mailto:dpo@floop.ai" className="text-primary hover:underline">dpo@floop.ai</a></li>
                <li>Utiliser le formulaire de contact sur notre site</li>
                <li>Accéder aux paramètres de votre compte pour certaines actions (suppression, export)</li>
              </ul>
              <p>
                Nous répondrons à votre demande dans un délai maximum de 30 jours. 
                Une pièce d'identité pourra vous être demandée pour vérifier votre identité.
              </p>
            </div>
            <div className="flex flex-wrap gap-4 mt-6">
              <Link to="/contact">
                <Button className="bg-gradient-floop hover:opacity-90 text-white rounded-xl">
                  Contacter le DPO
                </Button>
              </Link>
              <Link to="/settings">
                <Button variant="outline" className="rounded-xl">
                  Accéder à mes paramètres
                </Button>
              </Link>
            </div>
          </div>

          {/* Our Commitments */}
          <div className="bg-card border border-border rounded-2xl p-8">
            <h2 className="text-2xl font-bold text-foreground mb-6">Nos engagements</h2>
            <div className="space-y-6">
              <div>
                <h3 className="font-semibold text-foreground mb-2">Minimisation des données</h3>
                <p className="text-muted-foreground">
                  Nous ne collectons que les données strictement nécessaires au fonctionnement du service.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Sécurité des données</h3>
                <p className="text-muted-foreground">
                  Vos données sont chiffrées en transit et au repos. Nous utilisons les standards de sécurité les plus élevés.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Transparence</h3>
                <p className="text-muted-foreground">
                  Nous vous informons clairement de l'utilisation de vos données via notre politique de confidentialité.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Pas de transfert hors UE</h3>
                <p className="text-muted-foreground">
                  Vos données sont hébergées et traitées au sein de l'Union Européenne.
                </p>
              </div>
              <div>
                <h3 className="font-semibold text-foreground mb-2">Sous-traitants conformes</h3>
                <p className="text-muted-foreground">
                  Tous nos sous-traitants sont conformes au RGPD et ont signé des clauses contractuelles appropriées.
                </p>
              </div>
            </div>
          </div>

          {/* CNIL */}
          <div className="mt-12 text-center">
            <p className="text-muted-foreground">
              Vous avez également le droit de déposer une réclamation auprès de la CNIL :<br />
              <a href="https://www.cnil.fr" target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
                www.cnil.fr
              </a>
            </p>
          </div>
        </div>
      </section>

      <PremiumFooter />
    </div>
  );
};

export default RGPD;
